import React from 'react';
import { Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-600">Last updated: January 2025</p>
          </div>

          <Card>
            <CardContent className="p-6 sm:p-8 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Introduction</h2>
                <p className="text-slate-600 leading-relaxed">
                  ASHISH KUMAR ("we," "our," or "us") operates Peterhahn Fashion Coupon Club (the "App" and "Website"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website. Please read this privacy policy carefully. By using the App or Website, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Information We Collect</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  We may collect information about you in a variety of ways. The information we may collect includes:
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Personal Data</h3>
                <p className="text-slate-600 leading-relaxed mb-3">
                  We do not require you to create an account or provide personal information to use our basic services. However, if you choose to contact us, we may collect:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4 mb-4">
                  <li>Name and email address (when you contact us)</li>
                  <li>Any other information you voluntarily provide</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-2">Automatically Collected Data</h3>
                <p className="text-slate-600 leading-relaxed mb-3">
                  When you access our App or Website, we may automatically collect certain information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li>Device information (device type, operating system, unique device identifiers)</li>
                  <li>Log data (access times, pages viewed, IP address, referring URL)</li>
                  <li>Usage data (features used, actions taken within the App)</li>
                  <li>Location data (country/region level only, not precise location)</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">How We Use Your Information</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Personalize your experience and deliver relevant coupon offers</li>
                  <li>Respond to your comments, questions, and customer service requests</li>
                  <li>Monitor and analyze usage patterns and trends</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Disclosure of Your Information</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  We may share information we have collected about you in certain situations:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li><strong>Service Providers:</strong> We may share your information with third-party vendors who perform services on our behalf (analytics, hosting)</li>
                  <li><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to valid legal processes</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
                  <li><strong>With Your Consent:</strong> We may share your information with your consent for any other purpose</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  <strong>We do not sell your personal information to third parties.</strong>
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Third-Party Services</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Our App and Website may contain links to third-party websites and services (retailer websites, coupon sources). We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We may use third-party analytics services (such as Google Analytics) to help understand how our App is used. These services may collect information about your use of our App and other apps/websites.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Data Security</h2>
                <p className="text-slate-600 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee its absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Data Retention</h2>
                <p className="text-slate-600 leading-relaxed">
                  We retain your information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Your Rights and Choices</h2>
                <p className="text-slate-600 leading-relaxed mb-3">
                  Depending on your location, you may have certain rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 pl-4">
                  <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Opt-out:</strong> Opt-out of certain data collection practices</li>
                  <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  To exercise any of these rights, please contact us using the information provided below.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Children's Privacy</h2>
                <p className="text-slate-600 leading-relaxed">
                  Our App and Website are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn that we have collected personal information from a child under 13, we will take steps to delete such information as quickly as possible. If you believe we might have any information from or about a child under 13, please contact us immediately.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">International Data Transfers</h2>
                <p className="text-slate-600 leading-relaxed">
                  Your information may be transferred to and processed in countries other than the country in which you reside. These countries may have data protection laws that are different from the laws of your country. By using our App or Website, you consent to the transfer of your information to these countries.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Changes to This Privacy Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Contact Us</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-slate-100 rounded-lg p-4 space-y-2">
                  <p className="text-slate-700">
                    <strong>ASHISH KUMAR</strong>
                  </p>
                  <p className="text-slate-600">
                    FLAT 647, POCKET C4,<br />
                    SECTOR 34, ROHINI<br />
                    DELHI - 110039<br />
                    India (IN)
                  </p>
                  <p className="text-slate-600">
                    <strong>Developer:</strong> ivas.inc
                  </p>
                  <p className="text-slate-600">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:tb930654@gmail.com" className="text-slate-900 hover:underline">
                      tb930654@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-slate-100 border-l-4 border-slate-900 p-4 rounded">
                <p className="text-sm text-slate-700">
                  <strong>Consent:</strong> By using Peterhahn Fashion Coupon Club, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
